import type { SketchingSessionRoom } from './visit-counsel.types';

/**
 * 한국어 namePrefix → 정식 RoomType 역매핑 (방어용).
 *
 * 과거 mapHouseStructureFlowResult 매퍼 버그로 ROOM 등이 type='OTHER_SPACE',
 * namePrefix='1번 방' 형태로 저장된 sketching session 이 존재한다. 신규 데이터는
 * 매퍼에서 막혔지만 기존 데이터는 picker 분류·라벨 정책이 어긋나는 상태로 남아
 * 있어, 표시 단에서 namePrefix 의 한국어를 보고 정식 type 을 회복한다.
 */
const NAME_PREFIX_TO_TYPE: Record<string, string> = {
  현관: 'ENTRANCE',
  거실: 'LIVING_ROOM',
  주방: 'KITCHEN',
  방: 'ROOM',
  화장실: 'BATH_ROOM',
  발코니: 'BALCONY',
  베란다: 'BALCONY',
  세대창고: 'RESIDENCE_STORAGE',
};

/**
 * room.type 이 OTHER_SPACE 인데 namePrefix 가 알려진 한국어로 시작하면 그 type 으로 회복.
 * 그 외에는 room.type 그대로 반환.
 */
export function recoverRoomType(room: Pick<SketchingSessionRoom, 'type' | 'namePrefix'>): string {
  if (room.type !== 'OTHER_SPACE') return room.type;
  const cleaned = room.namePrefix.replace(/^\d+번\s*/, '').trim();
  return NAME_PREFIX_TO_TYPE[cleaned] ?? room.type;
}

/**
 * 단독 정책 판정 시 같은 그룹으로 묶을 키.
 *
 * - 회복된 type 이 OTHER_SPACE 인 경우(사용자가 "+ 그 외"로 추가한 자유 입력 공간)
 *   는 type 만으로는 부족하다 — "다락방" 과 "옷방" 은 같은 OTHER_SPACE 라도 서로 다른
 *   물리적 공간이므로 각각 단독으로 봐야 한다. 텍스트(stripped namePrefix) 까지 합쳐
 *   그룹 키를 만든다.
 * - 그 외 정식 type 은 type 만으로 그룹핑 (`거실`·`방`·`화장실` 등은 type 자체가
 *   의미를 갖는 분류이며, 사용자가 직접 텍스트를 입력하지 않는다).
 */
function singletonGroupKey(room: Pick<SketchingSessionRoom, 'type' | 'namePrefix'>): string {
  const recovered = recoverRoomType(room);
  if (recovered !== 'OTHER_SPACE') return recovered;
  const cleaned = room.namePrefix.replace(/^\d+번\s*/, '').trim();
  return `OTHER_SPACE|${cleaned || room.namePrefix.trim()}`;
}

/**
 * `room` 과 같은 그룹의 룸이 `allRooms` 안에 1개뿐이면 true.
 *
 * 단독 공간일 때 ordinal prefix("1번"·"4번") 를 떼고 베이스 한국어("거실"·"다락방")
 * 만 표시하기 위한 정책 판정에 사용된다 — 같은 그룹이 여러 개일 때만 "n번 X" 식
 * 표기가 의미가 있다는 디자인 결정 (사용자 정책).
 *
 * 그룹 키 (`singletonGroupKey`):
 *   - 정식 type (LIVING_ROOM/KITCHEN/ROOM/BATH_ROOM 등): type 만으로 비교.
 *   - OTHER_SPACE (사용자 자유 입력 "다락방"·"옷방" 등): type + 텍스트 까지 비교.
 *     → "다락방" 1개 + "옷방" 1개면 둘 다 단독, 같은 이름이 2개 이상일 때만 비단독.
 *
 * 회복된 type 으로 비교하므로 과거 매퍼 버그 데이터(type=OTHER_SPACE,
 * namePrefix="1번 방") 도 ROOM 그룹으로 올바르게 합산된다.
 */
export function isSingletonRoomType(
  room: SketchingSessionRoom,
  allRooms: ReadonlyArray<SketchingSessionRoom>
): boolean {
  const target = singletonGroupKey(room);
  let count = 0;
  for (const r of allRooms) {
    if (singletonGroupKey(r) === target) {
      count++;
      if (count > 1) return false;
    }
  }
  return count === 1;
}
